import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { NestedReportPending } from './-components/shared/nested-report-pending'
import { useMutation, useSuspenseQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { DescribeProfile, type DescribeIndicator } from './-components/orgs/describe-profile'
import { AdjustProfile, getAdjustState } from './-components/orgs/adjust-profile'
import { cn, getIndicatorsColor, withViewTransition } from '@/lib/utils'
import type { Indicators, IndicatorIndexes } from 'shared'
import { queryClient, trpc } from '@/main'
import { useTeam } from '@/hooks/use-team'
import { useLingui } from '@lingui/react/macro'
import { useIndicators } from '@/hooks/use-indicators'
import { Frame } from '@/components/ui/frame'
import { Button } from '@/components/ui/button'
import {
  AlertCircleIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Loader2Icon,
  PenLineIcon,
  UsersIcon,
  XIcon,
} from 'lucide-react'
import { useRootSearch } from '@/hooks/use-root-search'
import Steps, { type StepsColor } from './-components/shared/steps'
import type { DimensionHeader } from './-components/orgs/describe-profile'

export const Route = createFileRoute('/_app/teams/$teamId/evaluate')({
  component: RouteComponent,
  pendingComponent: NestedReportPending,
  // Shown immediately: at the router's default pendingMs the NAV STACK cannot push while the loaders run,
  // because the level it would push is the route being rendered — see the full story in jobs.$jobId.report.
  pendingMs: 0,
  pendingMinMs: 0,
})

type Indicator = 'competences' | 'values' | 'personality'
type Step = 'relevant' | 'irrelevant' | 'adjust'

interface MemberEvaluationStatus {
  id: number
  name: string | null
  image: string | null
  evaluated: boolean
  isSelf: boolean
}

const indicatorTypeColor: Record<Indicator, StepsColor> = {
  competences: 'amber',
  values: 'rose',
  personality: 'violet',
}

// Seed the relevant/non-relevant selection from prior scores: high (>=9) → relevant, low (<=3) →
// non-relevant, anything else (or absent) → unselected.
const buildSelected = (scores: readonly number[] | undefined): DescribeIndicator[] =>
  Array.from({ length: 18 }).map((_value, index) => {
    const score = scores?.[index]
    const selected = score == null ? undefined : score >= 9 ? 'relevant' : score <= 3 ? 'irrelevant' : undefined
    return { index, selected }
  })

const buildAdjust = (scores: readonly number[] | undefined) =>
  Array.from({ length: 18 }).map((_value, index) => ({ index, value: scores?.[index] ?? 0 }))

function RouteComponent() {
  const { t } = useLingui()
  const { teamId } = Route.useParams()

  const navigate = useNavigate()
  const { data: user } = useSuspenseQuery(trpc.auth.getUser.queryOptions())
  const { team } = useTeam()
  const { second } = useRootSearch()

  const memberId = second?.id as number | undefined
  const member = team?.members.find((m) => m.id === memberId)

  // Per-viewer evaluation status now comes precomputed from getTeam (membersEvaluationStatus): for
  // each member, whether the current viewer has fully evaluated them and whether it's the viewer.
  // oxlint-disable-next-line react-compiler-memo/no-manual-memo -- kept deliberately: an effect or context value here depends on this referential identity — without it react-hooks/exhaustive-deps flags the dependency as changing every render
  const membersStatus: MemberEvaluationStatus[] = useMemo(
    () => team?.membersEvaluationStatus ?? [],
    [team?.membersEvaluationStatus],
  )

  const evaluableMembers = membersStatus.filter((m) => !m.isSelf)
  const evaluatedCount = evaluableMembers.filter((m) => m.evaluated).length
  const totalToEvaluate = evaluableMembers.length

  // Whether the current viewer has fully evaluated the member under the cursor — read straight from
  // the per-viewer status getTeam precomputes, instead of re-deriving it from raw evaluations.
  const isUserEvaluatedThisMember = membersStatus.find((m) => m.id === memberId)?.evaluated ?? false

  // oxlint-disable-next-line react-compiler-memo/no-manual-memo -- kept deliberately: an effect or context value here depends on this referential identity — without it react-hooks/exhaustive-deps flags the dependency as changing every render
  const autoSelectMemberToEvaluate = useCallback(
    (statuses: MemberEvaluationStatus[]) => {
      // Pick the first evaluable member (not the viewer) the viewer hasn't finished evaluating yet.
      const memberNotEvaluated = statuses.find((m) => !m.isSelf && !m.evaluated)

      if (memberNotEvaluated) {
        navigate({
          to: '/teams/$teamId/evaluate',
          params: { teamId: teamId },
          search: (prev) => ({
            ...prev,
            first: { type: 'team', id: Number(teamId) },
            // teamId MUST travel with the member profile: the server's per-member permission gate is
            // keyed on the team membership, which it can only resolve when the profile carries teamId.
            // Omitting it let an evaluate-only member open a co-worker's report from the report tab.
            second: { id: memberNotEvaluated.id, type: 'member', teamId: Number(teamId) },
            selected: 'compare',
            type: 'competences',
          }),
        })
        return
      }

      const firstOtherMember = statuses.find((m) => !m.isSelf)

      navigate({
        to: '/teams/$teamId/evaluate',
        params: { teamId: teamId },
        search: !firstOtherMember
          ? (prev) => ({ ...prev, first: undefined, second: undefined, selected: 'first', type: 'competences' })
          : (prev) => ({
              ...prev,
              first: { type: 'team', id: Number(teamId) },
              // See above: teamId must accompany the member profile so the server can apply the
              // team permission gate when this profile is opened in the report tab.
              second: { id: firstOtherMember.id, type: 'member', teamId: Number(teamId) },
              selected: 'compare',
              type: 'competences',
            }),
      })
    },
    [navigate, teamId],
  )

  // Whether the URL already points at a concrete, evaluable member: a real member of this team who
  // isn't the current user. Auto-selection exists only to pick a member when you ENTER the page
  // without one chosen — it must not run when a valid member is already selected.
  const hasValidEvaluatee =
    memberId != null && memberId !== user?.id && (team?.members.some((m) => m.id === memberId) ?? false)

  const [firstLoad, setFirstLoad] = useState(true)
  useEffect(() => {
    // `useTeam` guarantees `team` is loaded by the time this renders, so no pending guard needed.
    if (!firstLoad) return
    setFirstLoad(false)
    // Don't re-issue the auto-select navigation when a valid member is already selected. The effect's
    // `firstLoad` guard is per-mount, so without this check leaving the page via a tab/sidebar link
    // would unmount→remount this route mid-transition, re-fire auto-select, and navigate straight back
    // to /evaluate — an infinite remount loop that froze the page and made every other tab unreachable.
    if (hasValidEvaluatee) return
    autoSelectMemberToEvaluate(membersStatus)
  }, [autoSelectMemberToEvaluate, firstLoad, hasValidEvaluatee, membersStatus])

  // The current user's prior scores for the member under evaluation, captured at render. Passed
  // to <EvaluationForm> which seeds its local editing state from them ONCE at mount (the form is
  // keyed on memberId). Later changes here — from background getTeam refetches — are ignored by
  // the mounted form, so a refetch can never clobber an in-progress evaluation.
  const meAsMember = team?.members.find((m) => m.id === user?.id)
  const priorEvaluations = meAsMember?.evaluations.filter((e) => e.memberId === memberId) ?? []
  const priorScores: Record<Indicator, readonly number[] | undefined> = {
    competences: priorEvaluations.find((e) => e.type === 'competences')?.competences,
    values: priorEvaluations.find((e) => e.type === 'values')?.competences,
    personality: priorEvaluations.find((e) => e.type === 'personality')?.competences,
  }

  if (team?.members.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 p-12 text-center">
        <div className="bg-muted rounded-full p-4">
          <UsersIcon className="text-muted-foreground size-8" />
        </div>
        <div>
          <h3 className="text-lg font-semibold">{t`No team members`}</h3>
          <p className="text-muted-foreground text-sm">{t`Add members to this team to start evaluations`}</p>
        </div>
      </div>
    )
  }

  if (memberId != null && user?.id != null && memberId === user.id) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 p-12 text-center">
        <div className="bg-muted rounded-full p-4">
          <AlertCircleIcon className="text-muted-foreground size-8" />
        </div>
        <div>
          <h3 className="text-lg font-semibold">{t`You can't evaluate yourself`}</h3>
          <p className="text-muted-foreground text-sm">{t`Select another team member to evaluate`}</p>
        </div>
      </div>
    )
  }

  if (memberId != null && !team?.members.find((m) => m.id === memberId)) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 p-12 text-center">
        <div className="bg-muted rounded-full p-4">
          <AlertCircleIcon className="text-muted-foreground size-8" />
        </div>
        <div>
          <h3 className="text-lg font-semibold">{t`Member not found`}</h3>
          <p className="text-muted-foreground text-sm">{t`The selected member is not part of this team`}</p>
        </div>
      </div>
    )
  }

  // A member may always make the FIRST evaluation of someone; repeating it needs the teamsReevaluate
  // permission. Companies and team owners aren't team members so they can always re-evaluate. This
  // per-viewer flag is now precomputed by getTeam (viewerCanReevaluate). When a member already
  // evaluated this person and can't reevaluate, the form is hidden (server rejects the write too).
  const canReevaluate = team?.viewerCanReevaluate ?? true
  const showForm = member != null && member.id !== user?.id && (canReevaluate || !isUserEvaluatedThisMember)

  const memberName = member?.name ?? ''

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-4 py-8 sm:px-6 lg:px-8">
      {/* Section Header */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-xl bg-indigo-500/10">
            <PenLineIcon className="size-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <h2 className="text-foreground text-xl font-semibold">
            {memberName ? t`Evaluate ${memberName}` : t`Describe profile`}
          </h2>
        </div>
        <p className="text-muted-foreground text-sm">
          {t`Choose their relevant and non-relevant indicators, then adjust the scores`}
        </p>
        <div className="flex items-center gap-3 py-1">
          <div className="bg-muted relative h-1.5 w-48 overflow-hidden rounded-full">
            <div
              className={cn(
                'absolute inset-y-0 left-0 rounded-full transition-[width] duration-500',
                evaluatedCount === totalToEvaluate && totalToEvaluate > 0 ? 'bg-emerald-500' : 'bg-indigo-500',
              )}
              style={{ width: `${totalToEvaluate > 0 ? (evaluatedCount / totalToEvaluate) * 100 : 0}%` }}
            />
          </div>
          <span className="text-muted-foreground text-xs tabular-nums">
            {evaluatedCount}/{totalToEvaluate} {t`members evaluated`}
          </span>
        </div>
      </div>

      {isUserEvaluatedThisMember && member?.id !== user?.id && (
        <div className="flex items-center gap-3 rounded-xl bg-amber-50 px-4 py-3 dark:bg-amber-950/30">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-amber-500/10">
            <AlertCircleIcon className="size-4.5 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="flex flex-1 flex-col gap-0.5">
            <p className="text-foreground text-sm font-medium">
              {t`This member has already been evaluated.`}{' '}
              <Link
                to="/teams/$teamId/report"
                params={{ teamId: teamId }}
                search={(prev) => ({
                  ...prev,
                  // Compare against the team as assessed by THIS evaluator (manual), not the team's
                  // self-assessment — an evaluate-only member (no teamsSeeOwns) may see the results of
                  // their own evaluations but not the self-assessments, so the owns baseline would block it.
                  first: {
                    type: 'team',
                    id: Number(teamId),
                    evaluation: 'manual',
                    members: user?.id != null ? [user.id] : undefined,
                  },
                  // Show as "assessed by some" with the current user (the evaluator) selected.
                  second:
                    memberId != null
                      ? {
                          type: 'member',
                          id: memberId,
                          teamId: Number(teamId),
                          evaluation: 'manual',
                          members: user?.id != null ? [user.id] : undefined,
                        }
                      : prev.second,
                  // Default to the evaluated member's own profile, not the team-vs-member
                  // comparison radar (QA: results should open on the person just evaluated).
                  selected: 'second',
                })}
                className="cursor-pointer underline underline-offset-2"
              >
                {t`View results`}
              </Link>
            </p>
            {canReevaluate && (
              <p className="text-muted-foreground text-xs">{t`You can repeat the evaluation of this person below`}</p>
            )}
          </div>
        </div>
      )}

      {showForm &&
        member && (
          // Keyed on the member being evaluated: switching members remounts the form with a fresh
          // seed, while background getTeam refetches (filter changes, post-save invalidations) leave
          // the in-progress selections untouched. See EvaluationForm for the seeding rationale.
          <EvaluationForm
            key={member.id}
            teamId={teamId}
            memberId={member.id}
            memberName={member.name ?? ''}
            userId={user?.id}
            priorScores={priorScores}
          />
        )}
    </div>
  )
}

interface EvaluationFormProps {
  teamId: string
  memberId: number
  memberName: string
  userId: number | undefined
  /** The current user's prior scores for this member; seeds the editing state once at mount. */
  priorScores: Record<Indicator, readonly number[] | undefined>
}

/**
 * The per-member evaluation wizard (relevant → non-relevant → adjust, across the three indicators).
 * All editing state lives here and is seeded ONCE from `priorScores` via lazy `useState`
 * initializers. The parent mounts this with `key={memberId}`, so:
 *  - switching members remounts with a fresh seed (no stale effect-driven reset), and
 *  - background getTeam refetches re-render the parent without resetting these selections.
 * This is the fix for the prior bug where an effect re-seeded on every `team.members` reference
 * change and silently wiped the manager's in-progress selections mid-flow.
 * On finishing the last indicator it navigates straight to the member's report.
 */
function EvaluationForm(props: EvaluationFormProps) {
  const { t } = useLingui()
  const { indicators } = useIndicators()
  const navigate = useNavigate()

  const [indicator, setIndicator] = useState<Indicator>('competences')
  const [step, setStep] = useState<Step>('relevant')

  const [_indicatorsCompetences, setIndicatorsCompetences] = useState<DescribeIndicator[]>(() =>
    buildSelected(props.priorScores.competences),
  )
  const [_adjustIndicatorsCompetences, setAdjustIndicatorsCompetences] = useState(() =>
    buildAdjust(props.priorScores.competences),
  )
  const [_indicatorsValues, setIndicatorsValues] = useState<DescribeIndicator[]>(() =>
    buildSelected(props.priorScores.values),
  )
  const [_adjustIndicatorsValues, setAdjustIndicatorsValues] = useState(() => buildAdjust(props.priorScores.values))
  const [_indicatorsPersonality, setIndicatorsPersonality] = useState<DescribeIndicator[]>(() =>
    buildSelected(props.priorScores.personality),
  )
  const [_adjustIndicatorsPersonality, setAdjustIndicatorsPersonality] = useState(() =>
    buildAdjust(props.priorScores.personality),
  )

  const decorate = (type: Indicator, value: DescribeIndicator) => ({
    ...value,
    name: '',
    description: '',
    ...indicators[type].competences[value.index],
    color: getIndicatorsColor(type, value.index).normal,
  })
  const decorateAdjust = (type: Indicator, value: { index: number; value: number }) => ({
    ...value,
    name: '',
    description: '',
    ...indicators[type].competences[value.index],
    color: getIndicatorsColor(type, value.index).normal,
  })

  const indicatorsCompetences = _indicatorsCompetences.map((v) => decorate('competences', v))
  const adjustIndicatorsCompetences = _adjustIndicatorsCompetences.map((v) => decorateAdjust('competences', v))
  const indicatorsValues = _indicatorsValues.map((v) => decorate('values', v))
  const adjustIndicatorsValues = _adjustIndicatorsValues.map((v) => decorateAdjust('values', v))
  const indicatorsPersonality = _indicatorsPersonality.map((v) => decorate('personality', v))
  const adjustIndicatorsPersonality = _adjustIndicatorsPersonality.map((v) => decorateAdjust('personality', v))

  useEffect(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.querySelector('.scrollable-area')?.scrollTo({ top: 0 })
        window.scrollTo({ top: 0 })
      })
    })
  }, [indicator, step])

  const { mutateAsync: calculateProfile, isPending: isCalculatingProfile } = useMutation(
    trpc.common.calculateProfile.mutationOptions(),
  )
  const { mutateAsync: evaluateMember, isPending: isSavingEvaluation } = useMutation(
    trpc.teams.evaluateMember.mutationOptions(),
  )

  const updateAdjust = async (type: Indicator) => {
    const indicatorsList =
      type === 'competences' ? indicatorsCompetences : type === 'values' ? indicatorsValues : indicatorsPersonality
    const competences = await calculateProfile({
      highs: indicatorsList.filter((ind) => ind.selected === 'relevant').map((ind) => ind.index as IndicatorIndexes),
      lows: indicatorsList.filter((ind) => ind.selected === 'irrelevant').map((ind) => ind.index as IndicatorIndexes),
    })

    if (type === 'competences') {
      setAdjustIndicatorsCompetences(competences.map((value, index) => ({ index, value })))
    } else if (type === 'values') {
      setAdjustIndicatorsValues(competences.map((value, index) => ({ index, value })))
    } else {
      setAdjustIndicatorsPersonality(competences.map((value, index) => ({ index, value })))
    }
  }

  const goTo = (nextIndicator: Indicator, nextStep: Step) => {
    const update = () => {
      setIndicator(nextIndicator)
      setStep(nextStep)
    }
    // Morph the indicator titles when moving between the select (irrelevant) and adjust
    // layouts of the same indicator; every other transition updates plainly.
    const isSelectAdjustMorph =
      nextIndicator === indicator &&
      ((step === 'irrelevant' && nextStep === 'adjust') || (step === 'adjust' && nextStep === 'irrelevant'))
    if (isSelectAdjustMorph) withViewTransition(update)
    else update()
  }

  const steps = {
    competences: {
      relevant: {
        title: t`Relevant competencies`,
        description: t`Select between three and six relevant competencies of this person`,
        step: 'relevant' as const,
        indicators: indicatorsCompetences,
        setIndicators: setIndicatorsCompetences,
        nextLabel: t`Continue to non-relevant`,
        onNextStep: () => goTo('competences', 'irrelevant'),
      },
      irrelevant: {
        title: t`Non-relevant competencies`,
        description: t`Select between three and six competencies not relevant of this person`,
        step: 'irrelevant' as const,
        indicators: indicatorsCompetences,
        setIndicators: setIndicatorsCompetences,
        previousLabel: t`Edit relevant`,
        nextLabel: t`Adjust scores`,
        isNextPending: isCalculatingProfile,
        onPreviousStep: () => goTo('competences', 'relevant'),
        onNextStep: async () => {
          await updateAdjust('competences')
          goTo('competences', 'adjust')
        },
      },
      adjust: {
        title: t`Adjust competencies`,
        description: t`Modify, if you consider it appropriate, the scores provided by the system`,
        indicators: adjustIndicatorsCompetences,
        setIndicators: setAdjustIndicatorsCompetences,
        previousLabel: t`Edit non-relevant`,
        nextLabel: t`Continue to values`,
        isNextPending: isSavingEvaluation,
        onPreviousStep: () => goTo('competences', 'irrelevant'),
        onNextStep: async () => {
          await evaluateMember({
            teamId: Number(props.teamId),
            competences: adjustIndicatorsCompetences.map((ind) => ind.value) as Indicators['competences'],
            evaluated: props.memberId,
            type: 'competences',
          })
          goTo('values', 'relevant')
        },
      },
    },
    values: {
      relevant: {
        title: t`Relevant values`,
        description: t`Select between three and six relevant values of this person`,
        step: 'relevant' as const,
        indicators: indicatorsValues,
        setIndicators: setIndicatorsValues,
        previousLabel: t`Back to competencies`,
        nextLabel: t`Continue to non-relevant`,
        onPreviousStep: () => goTo('competences', 'adjust'),
        onNextStep: () => goTo('values', 'irrelevant'),
      },
      irrelevant: {
        title: t`Non-relevant values`,
        description: t`Select between three and six values not relevant of this person`,
        step: 'irrelevant' as const,
        indicators: indicatorsValues,
        setIndicators: setIndicatorsValues,
        previousLabel: t`Edit relevant`,
        nextLabel: t`Adjust scores`,
        isNextPending: isCalculatingProfile,
        onPreviousStep: () => goTo('values', 'relevant'),
        onNextStep: async () => {
          await updateAdjust('values')
          goTo('values', 'adjust')
        },
      },
      adjust: {
        title: t`Adjust values`,
        description: t`Modify, if you consider it appropriate, the scores provided by the system`,
        indicators: adjustIndicatorsValues,
        setIndicators: setAdjustIndicatorsValues,
        previousLabel: t`Edit non-relevant`,
        nextLabel: t`Continue to traits`,
        isNextPending: isSavingEvaluation,
        onPreviousStep: () => goTo('values', 'irrelevant'),
        onNextStep: async () => {
          await evaluateMember({
            teamId: Number(props.teamId),
            competences: adjustIndicatorsValues.map((ind) => ind.value) as Indicators['competences'],
            evaluated: props.memberId,
            type: 'values',
          })
          goTo('personality', 'relevant')
        },
      },
    },
    personality: {
      relevant: {
        title: t`Relevant traits`,
        description: t`Select between three and six relevant traits of this person`,
        step: 'relevant' as const,
        indicators: indicatorsPersonality,
        setIndicators: setIndicatorsPersonality,
        previousLabel: t`Back to values`,
        nextLabel: t`Continue to non-relevant`,
        onPreviousStep: () => goTo('values', 'adjust'),
        onNextStep: () => goTo('personality', 'irrelevant'),
      },
      irrelevant: {
        title: t`Non-relevant traits`,
        description: t`Select between three and six traits not relevant of this person`,
        step: 'irrelevant' as const,
        indicators: indicatorsPersonality,
        setIndicators: setIndicatorsPersonality,
        previousLabel: t`Edit relevant`,
        nextLabel: t`Adjust scores`,
        isNextPending: isCalculatingProfile,
        onPreviousStep: () => goTo('personality', 'relevant'),
        onNextStep: async () => {
          await updateAdjust('personality')
          goTo('personality', 'adjust')
        },
      },
      adjust: {
        title: t`Adjust traits`,
        description: t`Modify, if you consider it appropriate, the scores provided by the system`,
        indicators: adjustIndicatorsPersonality,
        setIndicators: setAdjustIndicatorsPersonality,
        previousLabel: t`Edit non-relevant`,
        isNextPending: isSavingEvaluation,
        onPreviousStep: () => goTo('personality', 'irrelevant'),
        onNextStep: async () => {
          await evaluateMember({
            teamId: Number(props.teamId),
            competences: adjustIndicatorsPersonality.map((ind) => ind.value) as Indicators['competences'],
            evaluated: props.memberId,
            type: 'personality',
          })
          // Go straight to the just-evaluated member's report instead of an intermediate "done" card.
          // Navigate FIRST so the evaluate page unmounts cleanly: the getTeam refetch below would
          // otherwise flip showForm off and briefly flash the "already evaluated" notice. The report
          // fetches fresh data for these (new) search params.
          navigate({
            to: '/teams/$teamId/report',
            params: { teamId: props.teamId },
            search: (prev) => ({
              ...prev,
              // Compare against the team as assessed by THIS evaluator (manual), not the team's
              // self-assessment, so an evaluate-only member (no teamsSeeOwns) can still see it.
              first: {
                type: 'team',
                id: Number(props.teamId),
                evaluation: 'manual',
                members: props.userId != null ? [props.userId] : undefined,
              },
              // The just-evaluated member as "assessed by some", with the current user selected.
              second: {
                type: 'member',
                id: props.memberId,
                teamId: Number(props.teamId),
                evaluation: 'manual',
                members: props.userId != null ? [props.userId] : undefined,
              },
              // Open on the evaluated member's own profile, not the team-vs-member comparison radar.
              selected: 'second',
              type: 'competences',
            }),
          })
          await Promise.all([
            queryClient.invalidateQueries(trpc.teams.getTeam.queryFilter()),
            queryClient.invalidateQueries(trpc.teams.getTeams.queryFilter()),
            queryClient.invalidateQueries(trpc.reports.getReport.queryFilter()),
          ])
        },
        buttonText: t`Finish`,
      },
    },
  }

  const stepsData = [
    { key: 'competences-relevant', title: steps.competences.relevant.title },
    { key: 'competences-irrelevant', title: steps.competences.irrelevant.title },
    { key: 'competences-adjust', title: steps.competences.adjust.title },
    { key: 'values-relevant', title: steps.values.relevant.title },
    { key: 'values-irrelevant', title: steps.values.irrelevant.title },
    { key: 'values-adjust', title: steps.values.adjust.title },
    { key: 'personality-relevant', title: steps.personality.relevant.title },
    { key: 'personality-irrelevant', title: steps.personality.irrelevant.title },
    { key: 'personality-adjust', title: steps.personality.adjust.title },
  ]
  const stepIndexMap: Record<string, number> = {
    'competences-relevant': 0,
    'competences-irrelevant': 1,
    'competences-adjust': 2,
    'values-relevant': 3,
    'values-irrelevant': 4,
    'values-adjust': 5,
    'personality-relevant': 6,
    'personality-irrelevant': 7,
    'personality-adjust': 8,
  }

  const currentStepData = step === 'adjust' ? steps[indicator].adjust : steps[indicator][step]
  const isLastStep = indicator === 'personality' && step === 'adjust'

  const dimensionsList = indicators[indicator].dimensions
  const dimensions: DimensionHeader[] = dimensionsList.map((dim, i) => ({
    name: dim.name,
    color: getIndicatorsColor(indicator, i * 6).normal,
    iconSrc: `/icons/${indicator}/dimensions-${i}.svg`,
  }))

  return (
    <Frame className="flex flex-col gap-4">
      <div className="flex flex-col gap-5 px-4 pt-5 pb-5 sm:px-6 sm:pt-6">
        <Steps
          steps={stepsData}
          stepIndex={stepIndexMap[`${indicator}-${step}`] ?? 0}
          color={indicatorTypeColor[indicator]}
          className="mx-0 w-60 sm:w-72"
        />
        <div className="flex min-w-0 flex-col gap-1">
          <h2 className="min-w-0 text-lg leading-tight font-semibold sm:text-xl">{currentStepData.title}</h2>
          <p className="text-muted-foreground min-w-0 truncate text-xs sm:text-sm">
            <span className="text-foreground font-medium">{props.memberName}</span>
          </p>
        </div>
        <p className="text-muted-foreground text-sm leading-relaxed whitespace-pre-wrap">
          {currentStepData.description}
        </p>
      </div>

      <div className="px-4 pb-4 sm:px-6">
        {step === 'adjust' ? (
          <AdjustProfile
            {...steps[indicator].adjust}
            dimensions={dimensions}
            className=""
            showTitle={false}
            hideActions
            titleViewTransition
          />
        ) : (
          <DescribeProfile
            {...steps[indicator][step]}
            dimensions={dimensions}
            className=""
            showTitle={false}
            hideActions
            titleViewTransition
          />
        )}
      </div>

      <FormFooter step={step} isLastStep={isLastStep} stepData={currentStepData} />
    </Frame>
  )
}

type FormStepData = {
  indicators: { selected?: 'relevant' | 'irrelevant' | undefined; value?: number }[]
  onPreviousStep?: () => void
  onNextStep?: () => void
  nextLabel?: string
  previousLabel?: string
  isNextPending?: boolean
  buttonText?: string
}

function FormFooter(props: { step: Step; isLastStep: boolean; stepData: FormStepData }) {
  const { t } = useLingui()
  const isPending = props.stepData.isNextPending ?? false

  const previousButton = props.stepData.onPreviousStep ? (
    <Button type="button" variant="ghost" onClick={props.stepData.onPreviousStep} disabled={isPending}>
      <ChevronLeftIcon />
      {props.stepData.previousLabel ?? t`Previous`}
    </Button>
  ) : (
    <div />
  )

  if (props.step === 'adjust') {
    const indicators = props.stepData.indicators as { value: number }[]
    const adjustState = getAdjustState(indicators)
    const difference = adjustState.difference
    const plural = difference === 1 ? '' : 's'
    const finishLabel =
      props.stepData.buttonText ?? props.stepData.nextLabel ?? (props.isLastStep ? t`Finish` : t`Next`)
    const nextLabel = adjustState.isOver
      ? t`Remove ${difference} point${plural}`
      : adjustState.isUnder
        ? t`Add ${difference} point${plural}`
        : finishLabel

    const isDisabled = !adjustState.isValid || isPending

    return (
      <div className="flex shrink-0 items-center justify-between gap-3 px-4 py-4 sm:px-6">
        {previousButton}
        <Button
          type="button"
          onClick={isDisabled ? undefined : props.stepData.onNextStep}
          disabled={isDisabled}
          className={cn('disabled:opacity-100', !adjustState.isValid && !isPending && 'bg-destructive')}
        >
          {nextLabel}
          {isPending ? (
            <Loader2Icon className="animate-spin" />
          ) : adjustState.isValid ? (
            props.isLastStep ? (
              <CheckIcon />
            ) : (
              <ChevronRightIcon />
            )
          ) : (
            <XIcon />
          )}
        </Button>
      </div>
    )
  }

  const indicators = props.stepData.indicators as { selected?: 'relevant' | 'irrelevant' | undefined }[]
  const selectedCount = indicators.filter((i) => i.selected === props.step).length
  const canAdvance = selectedCount >= 3 && selectedCount <= 6
  const isDisabled = !canAdvance || isPending

  return (
    <div className="flex shrink-0 items-center justify-between gap-3 px-4 py-4 sm:px-6">
      {previousButton}
      <Button type="button" onClick={props.stepData.onNextStep} disabled={isDisabled}>
        {props.stepData.nextLabel ?? t`Next`}
        {isPending ? <Loader2Icon className="animate-spin" /> : <ChevronRightIcon />}
      </Button>
    </div>
  )
}
